import SftGlobalData from '../../library/SftGlobalData';

/**
 * Shared helpers for the Verlofplanner (route "team").
 * The team token from login is the only identity the backend accepts.
 */

export const teamPost = (action, token, body = {}) =>
    SftGlobalData.post('team&action=' + action, { team_token: token, ...body });

const pad = (n) => String(n).padStart(2, '0');

export const isoDate = (date) => date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());

export const parseDate = (text) => {
    const [y, m, d] = text.substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
};

/** ISO weekday: 1 = Monday ... 7 = Sunday. */
export const isoWeekday = (text) => ((parseDate(text).getDay() + 6) % 7) + 1;

export const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

/** Decimal hours as the leave sheets write them: 8,4 u (nl), 8,4 h (fr), 8.4 h (en). */
export const hours = (value, lang) => {
    if (value === null || value === undefined || isNaN(value)) return '—';
    const n = Math.round(value * 100) / 100;
    const text = (n < 0 ? '−' : '') + Math.abs(n).toLocaleString(lang === 'en' ? 'en-GB' : lang + '-BE', { maximumFractionDigits: 2 });
    return text + (lang === 'nl' ? ' u' : ' h');
};

/** "2026-11-05" -> "5/11" */
export const shortDate = (text) => {
    if (!text) return '';
    const d = parseDate(text);
    return d.getDate() + '/' + (d.getMonth() + 1);
};

export const locale = (lang) => (lang === 'en' ? 'en-GB' : (lang || 'nl') + '-BE');

/** "2026-11-27" -> "vr 27 nov" */
export const niceDate = (text, lang) => {
    if (!text) return '';
    return parseDate(text).toLocaleDateString(locale(lang), { weekday: 'short', day: 'numeric', month: 'short' });
};

/** A period: "vr 27 nov" or "ma 2 nov – vr 6 nov". */
export const period = (from, to, lang) => (from === to || !to ? niceDate(from, lang) : niceDate(from, lang) + ' – ' + niceDate(to, lang));

export const addDays = (text, days) => {
    const d = parseDate(text);
    d.setDate(d.getDate() + days);
    return isoDate(d);
};

export const mondayOf = (text) => {
    const d = parseDate(text);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return isoDate(d);
};

/** Easter Sunday of a year (Gregorian, anonymous algorithm) as Y-m-d. */
const easterSunday = (y) => {
    const a = y % 19; const b = Math.floor(y / 100); const c = y % 100;
    const h = (19 * a + b - Math.floor(b / 4) - Math.floor((b - Math.floor((b + 8) / 25) + 1) / 3) + 15) % 30;
    const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return isoDate(new Date(y, month - 1, day, 12));
};

/**
 * School holidays of the Flemish Community for the school year calendar of
 * year y (Belgium, SoftTouch Vlaanderen): [{from, to, key}], to inclusive.
 *  - krokus: 1 week, 7 weeks before Easter Monday;
 *  - Pasen: 2 weeks from the first Monday of April; Easter in March: from
 *    Easter Monday; Easter after 15 April: from the second Monday before Easter;
 *  - zomer: 1 July - 31 August;
 *  - herfst: the week of 1 November (1 November on a Sunday: the week after);
 *  - kerst: 2 weeks from the Monday of the week of 25 December (25 December
 *    on a Saturday or Sunday: from the Monday after).
 */
export const schoolHolidays = (y) => {
    const easter = easterSunday(y);
    const easterMonday = addDays(easter, 1);
    let paas;
    if (easter.substring(5, 7) === '03') {
        paas = easterMonday;
    } else if (easter > y + '-04-15') {
        paas = addDays(easter, -13);
    } else {
        const firstApril = parseDate(y + '-04-01');
        paas = addDays(y + '-04-01', (8 - firstApril.getDay()) % 7);
    }
    const nov1 = y + '-11-01';
    const herfst = isoWeekday(nov1) === 7 ? addDays(nov1, 1) : mondayOf(nov1);
    const xmas = y + '-12-25';
    const kerst = isoWeekday(xmas) >= 6 ? addDays(xmas, 8 - isoWeekday(xmas)) : mondayOf(xmas);
    return [
        { key: 'krokus', from: addDays(easterMonday, -49), to: addDays(easterMonday, -43) },
        { key: 'pasen', from: paas, to: addDays(paas, 13) },
        { key: 'zomer', from: y + '-07-01', to: y + '-08-31' },
        { key: 'herfst', from: herfst, to: addDays(herfst, 6) },
        { key: 'kerst', from: kerst, to: addDays(kerst, 13) },
    ];
};

/** The school holiday a day falls in (Flemish Community), or null. Christmas of the year before runs into January. */
export const schoolHolidayOn = (day) => {
    const y = parseInt(day.substring(0, 4), 10);
    const hit = [...schoolHolidays(y - 1), ...schoolHolidays(y)].find((h) => day >= h.from && day <= h.to);
    return hit ? hit.key : null;
};

export const schoolHolidayLabel = (t, key) => ({
    krokus: t('team_school_krokus', 'Carnival holidays'),
    pasen: t('team_school_pasen', 'Easter holidays'),
    zomer: t('team_school_zomer', 'Summer holidays'),
    herfst: t('team_school_herfst', 'Autumn holidays'),
    kerst: t('team_school_kerst', 'Christmas holidays'),
}[key] || key);

/** Roster row of a person for a day, or null (day off by roster). */
export const rosterDay = (roster, day) => {
    let best = null;
    (roster || []).forEach((r) => {
        if (r.valid_from <= day && (!best || r.valid_from > best)) best = r.valid_from;
    });
    if (!best) return null;
    const weekday = isoWeekday(day);
    return (roster || []).find((r) => r.valid_from === best && r.weekday === weekday) || null;
};

/** Colours per kind; same palette as the Verlofplanner proposal. */
const KIND_COLORS = {
    LEAVE: ['#6AA84F', '#7DBB5F'],
    RECUP: ['#D9483B', '#E8645A'],
    SICK: ['#E8883A', '#F09A52'],
    REPLACEMENT: ['#F2C230', '#E9C24A'],
    SPECIAL: ['#E032D6', '#E563DC'],
    INSTALL: ['#1C8C8C', '#3FB3B3'],
    INTERVENTION: ['#1C8C8C', '#3FB3B3'],
    AWAY: ['#A3ACB7', '#6C7683'],
};

export const kindColor = (kind, theme) => (KIND_COLORS[kind] || KIND_COLORS.AWAY)[theme === 'dark' ? 1 : 0];

export const KINDS_LEGEND = ['LEAVE', 'RECUP', 'REPLACEMENT', 'SICK', 'SPECIAL', 'INSTALL', 'AWAY'];

export const kindLabel = (t, kind) => ({
    LEAVE: t('team_kind_leave', 'Leave'),
    RECUP: t('team_kind_recup', 'Recup'),
    REPLACEMENT: t('team_kind_replacement', 'Replacement day'),
    SICK: t('team_kind_sick', 'Sick'),
    SPECIAL: t('team_kind_special', 'Special leave'),
    INSTALL: t('team_kind_install', 'Installation'),
    INTERVENTION: t('team_kind_intervention', 'Intervention'),
    AWAY: t('team_kind_away', 'Away'),
}[kind] || kind);

export const departmentLabel = (t, dept) => ({
    MGMT: t('team_dept_mgmt', 'Management'),
    DEV: t('team_dept_dev', 'Development'),
    ADMIN: t('team_dept_admin', 'Administration'),
    MARKETING: t('team_dept_marketing', 'Marketing'),
    SUPPORT: t('team_dept_support', 'Support'),
}[dept] || dept);

// Order of the groups in the calendar and the colleague pickers.
export const DEPARTMENTS = ['SUPPORT', 'DEV', 'MARKETING', 'ADMIN', 'MGMT'];

export const specialLabel = (t, code) => ({
    KLEIN_VERLET: t('team_special_klein_verlet', 'Short leave (wedding, death, ceremony)'),
    GEBOORTE: t('team_special_geboorte', 'Birth leave'),
    ADOPTIE: t('team_special_adoptie', 'Adoption or foster leave'),
    OUDERSCHAP: t('team_special_ouderschap', 'Parental leave'),
    FAMILIAAL: t('team_special_familiaal', 'Leave for compelling reasons'),
    ZORG: t('team_special_zorg', 'Care leave'),
    MANTELZORG: t('team_special_mantelzorg', 'Informal care, palliative or medical assistance leave'),
    OPLEIDING: t('team_special_opleiding', 'Flemish training leave'),
    ONBETAALD: t('team_special_onbetaald', 'Unpaid leave'),
    ANDERE: t('team_special_andere', 'Other'),
}[code] || code || '');

/** Readable message for a failed team call. */
export const teamError = (t, response) => {
    const key = response?.translation_key || response?.data?.translation_key;
    const known = {
        team_session_invalid: t('team_err_session', 'Your planner session has expired. Log in again to continue.'),
        team_not_allowed: t('team_err_not_allowed', 'You are not allowed to do this.'),
        team_invalid_input: t('team_err_invalid', 'Check the values you entered.'),
        team_import_file_invalid: t('team_err_file', 'This file is not a leave sheet that can be read (.xlsx).'),
        team_import_sheet_missing: t('team_err_sheet', 'The sheet "Blad1" was not found in this file.'),
        team_no_roster: t('team_err_no_roster', 'This person has no roster yet.'),
        team_save_failed: t('team_err_save', 'Saving failed. Try again.'),
        team_overlap: t('team_err_overlap', 'There is already something planned on one of these days.'),
        team_remark_required: t('team_err_remark', 'A remark is required for special leave.'),
        team_no_workdays: t('team_err_no_workdays', 'There are no working days in this period.'),
        team_in_past: t('team_err_past', 'This period lies in the past.'),
        team_invalid_period: t('team_err_period', 'Choose a period of at most two months.'),
        team_cancel_too_late: t('team_err_cancel_late', 'Too late to cancel yourself; ask management.'),
        team_not_found: t('team_err_not_found', 'This is no longer waiting; refresh.'),
        team_reason_required: t('team_err_reason', 'Not every guideline is met: give a reason for the exception.'),
        team_not_workday: t('team_err_not_workday', 'Not a working day for this colleague.'),
        team_pending_on_day: t('team_err_pending_day', 'A request is waiting for this day; handle it under Approve.'),
        team_invalid_hours: t('team_err_hours', 'Hours: for one day, more than 0 and at most the roster hours.'),
        team_swap_pending: t('team_err_swap_pending', 'A weekend swap is already waiting for this week.'),
        team_not_your_weekend: t('team_err_not_your_weekend', 'You can only swap a weekend you have.'),
        team_swap_outdated: t('team_err_swap_outdated', 'The duty weeks changed in the meantime; ask again.'),
        team_no_schedule: t('team_err_no_schedule', 'This week has no weekly schedule yet.'),
        team_attachment_type: t('team_err_attachment_type', 'Attachment: a PDF, a photo (jpg, png, heic) or a Word file.'),
        team_attachment_size: t('team_err_attachment_size', 'Attachment: at most 5 MB.'),
        team_self_lockout: t('team_err_self_lockout', 'You cannot take away your own staff right or deactivate yourself.'),
        team_account_taken: t('team_err_account_taken', 'This login already belongs to another colleague.'),
        team_invalid_roster: t('team_err_roster', 'Check the times, break and recup (end after start, at most 12 h a day, at least one working day).'),
        team_last_roster: t('team_err_last_roster', 'The only roster of a colleague cannot be removed.'),
        team_fill_not_next: t('team_err_fill_not_next', 'Only the first week without a schedule, after a filled week, can be filled in automatically.'),
        team_attachment_already: t('team_err_attachment_already', 'A certificate was already sent for this sick report.'),
        team_no_email: t('team_err_no_email', 'This colleague has no e-mail address in the planner.'),
        team_attachment_required: t('team_err_attachment_required', 'Add the doctor\'s certificate: an attachment is required when you report sick.'),
    };
    return known[key] || response?.message || t('errorOccured', 'Something went wrong');
};

export const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.heif,.doc,.docx';
export const ATTACHMENT_MAX = 5 * 1024 * 1024;

/** Read a chosen file as {name, data: base64} for the planner (mailed, not stored). */
export const readAttachment = (file) => new Promise((resolve, reject) => {
    if (!file) { resolve(null); return; }
    if (file.size > ATTACHMENT_MAX) { reject(new Error('size')); return; }
    const reader = new FileReader();
    reader.onload = () => {
        const text = String(reader.result || '');
        resolve({ name: file.name, data: text.substring(text.indexOf(',') + 1) });
    };
    reader.onerror = () => reject(new Error('read'));
    reader.readAsDataURL(file);
});
